import { GroupBar } from "@/pages/tina/groups/components/group-bar";
import { buildGroupColors } from "@/pages/tina/groups/lib/group-color";
import { buildWeeklyLayout } from "@/pages/tina/groups/lib/weekly-layout";
import type { TinaSiteGroup } from "@/types/tina";

export interface WeeklyCalendarProps {
  groups: TinaSiteGroup[];
  onEdit: (id: string) => void;
  onRunNow: (id: string) => void;
  onDelete: (id: string) => void;
  busyId?: string | null;
}

const ISO_BY_SHORT: Record<string, number> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

/** ISO weekday (1=Mon … 7=Sun) for "now" in the fixed ops timezone. */
function isoTodayInAthens(): number {
  const short = new Date().toLocaleDateString("en-US", {
    timeZone: "Europe/Athens",
    weekday: "short",
  });
  return ISO_BY_SHORT[short] ?? 0;
}

// Non-tokened dark-theme values from src/App.css `.dark` (no Tailwind alias).
const BORDER_50 = "oklch(0.275 0 0 / 0.5)";
const BORDER_40 = "oklch(0.275 0 0 / 0.4)";
const INACTIVE_CELL_BG = "oklch(0.125 0 0)";
const INACTIVE_DAY_TEXT = "oklch(0.442 0 0)";
const ACTIVE_DAY_TEXT = "oklch(0.708 0 0)";
const TODAY_TEXT = "oklch(0.985 0 0)";
const TODAY_DOT = "oklch(0.81 0.1 252)";
const FOOTER_TEXT = "oklch(0.556 0 0)";

export function WeeklyCalendar({
  groups,
  onEdit,
  onRunNow,
  onDelete,
  busyId = null,
}: WeeklyCalendarProps) {
  const { columns, gridTemplateColumns, rows } = buildWeeklyLayout(groups);
  // Colour by display order (rows top-to-bottom) so vertically adjacent bars
  // land ~137.5° apart on the wheel and never look alike.
  const colors = buildGroupColors(
    rows.flatMap((r) => (r.kind === "group" ? [r.group.id] : [])),
  );
  const todayIso = isoTodayInAthens();
  const isEmpty = groups.length === 0;

  // Empty state keeps the 7-column header with all columns equal.
  const headerCols = isEmpty ? "66px repeat(7, 1fr)" : gridTemplateColumns;

  return (
    <div className="flex flex-col">
      {/* Day header */}
      <div className="grid pb-2" style={{ gridTemplateColumns: headerCols }}>
        <span />
        {columns.map((col) => {
          const isToday = !isEmpty && col.value === todayIso;
          return (
            <span
              key={col.value}
              data-testid={`weekday-${col.value}`}
              className="flex items-center justify-center gap-1.5 py-1.5 text-[13px]"
              style={{
                fontWeight: isToday ? 600 : 500,
                color: isToday
                  ? TODAY_TEXT
                  : col.active
                    ? ACTIVE_DAY_TEXT
                    : INACTIVE_DAY_TEXT,
              }}
            >
              {isToday && (
                <span
                  className="h-[5px] w-[5px] rounded-full"
                  style={{ background: TODAY_DOT }}
                />
              )}
              {col.short}
            </span>
          );
        })}
      </div>

      {isEmpty ? (
        <div
          className="py-6 text-sm text-muted-foreground"
          style={{ borderTop: `1px solid ${BORDER_50}` }}
        >
          No groups yet
        </div>
      ) : (
        <div className="flex flex-col">
          {rows.map((row, index) =>
            row.kind === "gap" ? (
              <div
                key={`gap-${index}`}
                data-testid={`gap-row-${index}`}
                className="grid items-center"
                style={{
                  gridTemplateColumns,
                  borderTop: `1px solid ${BORDER_50}`,
                }}
              >
                <span
                  className="whitespace-nowrap text-[10.5px] tabular-nums"
                  style={{
                    gridColumn: 1,
                    gridRow: 1,
                    color: INACTIVE_DAY_TEXT,
                  }}
                >
                  {row.label}
                </span>
                {columns.map((col, i) => (
                  <span
                    key={col.value}
                    style={{
                      gridColumn: i + 2,
                      gridRow: 1,
                      height: 24,
                      borderLeft: `1px solid ${BORDER_40}`,
                      background: col.active ? undefined : INACTIVE_CELL_BG,
                    }}
                  />
                ))}
                <span
                  className="flex items-center justify-center text-[10.5px]"
                  style={{
                    gridColumn: "2 / -1",
                    gridRow: 1,
                    zIndex: 1,
                    color: INACTIVE_DAY_TEXT,
                  }}
                >
                  no runs
                </span>
              </div>
            ) : (
              <div
                key={row.group.id}
                data-testid={`group-row-${row.group.id}`}
                className="grid items-center"
                style={{
                  gridTemplateColumns,
                  borderTop: `1px solid ${BORDER_50}`,
                }}
              >
                <span
                  className="text-[12.5px] font-semibold tabular-nums text-muted-foreground"
                  style={{ gridColumn: 1, gridRow: 1 }}
                >
                  {row.hourLabel}
                </span>
                {columns.map((col, i) => (
                  <span
                    key={col.value}
                    style={{
                      gridColumn: i + 2,
                      gridRow: 1,
                      height: 44,
                      borderLeft: `1px solid ${BORDER_40}`,
                      background: col.active ? undefined : INACTIVE_CELL_BG,
                    }}
                  />
                ))}
                <GroupBar
                  group={row.group}
                  color={colors.get(row.group.id)}
                  gridColumn={row.gridColumn}
                  segments={row.segments}
                  minuteLabel={row.minuteLabel}
                  onEdit={onEdit}
                  onRunNow={onRunNow}
                  onDelete={onDelete}
                  isBusy={busyId === row.group.id}
                />
              </div>
            ),
          )}
        </div>
      )}

      {!isEmpty && (
        <div className="pt-2.5 text-xs" style={{ color: FOOTER_TEXT }}>
          One row per group, sorted by time · a bar covers the days it runs ·
          hover a bar to run now · menu: Edit, Delete
        </div>
      )}
    </div>
  );
}
